import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ChevronLeft, Flame, Trophy, Award, Loader2, Plus, EyeOff, Eye, MoreHorizontal, Users,
} from "lucide-react";
import { REACTION_EMOJIS, REACTION_LABEL } from "@/lib/reactions";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

function EventCopy({ event }) {
  const p = event.payload || {};
  const handle = event.author?.display_handle || "Someone";
  switch (event.kind) {
    case "badge_unlocked":
      return (
        <span>
          <strong>{handle}</strong> unlocked the{" "}
          <span className="font-semibold">{p.badge_emoji} {p.badge_name}</span> badge
        </span>
      );
    case "streak_milestone":
      return (
        <span>
          <strong>{handle}</strong> hit a <span className="font-semibold">{p.days}-day streak</span> 🔥
        </span>
      );
    case "mock_exam_pass":
      return (
        <span>
          <strong>{handle}</strong> passed a <span className="font-semibold">{p.length}-question</span> mock exam ·
          <span className="font-semibold"> {p.accuracy}%</span>
        </span>
      );
    default:
      return <span><strong>{handle}</strong> did something</span>;
  }
}

function EventIcon({ kind }) {
  const cls = "w-4 h-4";
  if (kind === "badge_unlocked") return <Award className={cls} />;
  if (kind === "streak_milestone") return <Flame className={cls} />;
  if (kind === "mock_exam_pass") return <Trophy className={cls} />;
  return null;
}

export default function FeedPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/feed");
      setEvents(r.data.events || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Optimistic reaction — flip the bucket locally, fire the API. Refresh
  // silently if the server diverges so we never show a stale count.
  const react = async (event, emoji) => {
    setEvents((cur) => cur.map((e) => {
      if (e.id !== event.id) return e;
      const bucket = e.reactions.find((r) => r.emoji === emoji);
      let next;
      if (bucket) {
        const updated = { ...bucket, me: !bucket.me, count: bucket.count + (bucket.me ? -1 : 1) };
        next = e.reactions.map((r) => r.emoji === emoji ? updated : r).filter((r) => r.count > 0);
      } else {
        next = [...e.reactions, { emoji, count: 1, me: true }];
      }
      return { ...e, reactions: next };
    }));
    try {
      await api.post(`/feed/${event.id}/react/${encodeURIComponent(emoji)}`);
    } catch (err) {
      toast.error("Couldn't react");
      load();
    }
  };

  const hide = async (event) => {
    const willHide = event.visibility !== "hidden";
    if (willHide && !confirm("Hide this post from your friends' feed? You can unhide later.")) return;
    try {
      await api.put(`/feed/${event.id}/visibility`, { hidden: willHide });
      toast.success(willHide ? "Hidden from friends" : "Visible to friends");
      load();
    } catch (e) {
      toast.error("Could not update visibility");
    }
  };

  const remove = async (event) => {
    if (!confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/feed/${event.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="feed-page">
      <Link to="/student/friends" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to Friends
      </Link>

      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Friend Updates
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Activity Feed</h1>
        <p className="text-slate-600 text-sm mt-1">
          Celebrate your friends' wins. Badges, streaks, and mock exams — kindness only.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : events.length === 0 ? (
        <Card className="p-8 text-center border-slate-200" data-testid="feed-empty-state">
          <div className="mx-auto w-12 h-12 grid place-items-center rounded-full bg-indigo-100 text-indigo-700 mb-3">
            <Users className="w-6 h-6" />
          </div>
          <div className="text-slate-800 font-semibold text-base">No friend updates yet</div>
          <div className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            Your feed lights up when a friend earns a badge, hits a 7-day+ streak, or passes a mock exam.
            If you already have friends, ask them to complete a mock exam or keep studying daily — new activity
            usually appears within a minute.
          </div>
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            <Link to="/student/friends">
              <Button variant="outline" data-testid="feed-find-friends-btn">
                <Users className="w-4 h-4 mr-2" /> Manage friends
              </Button>
            </Link>
            <Link to="/student/study">
              <Button data-testid="feed-start-studying-btn">
                <Flame className="w-4 h-4 mr-2" /> Start a streak
              </Button>
            </Link>
          </div>
          <div className="text-[11px] text-slate-400 mt-4">
            Tip: Streak milestones trigger at 3, 7, 14, 30, 60, 100+ days.
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((e) => (
            <Card key={e.id} className="p-4 border-slate-200" data-testid={`feed-event-${e.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 grid place-items-center rounded-full bg-indigo-100 text-indigo-700 shrink-0">
                  <EventIcon kind={e.kind} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="text-sm text-slate-800 leading-relaxed">
                      <EventCopy event={e} />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-[11px] text-slate-500 tabular-nums">{timeAgo(e.created_at)}</span>
                      {e.is_self && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`feed-more-${e.id}`}>
                              <MoreHorizontal className="w-3.5 h-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => hide(e)} data-testid={`feed-hide-${e.id}`}>
                              {e.visibility === "hidden" ? (
                                <><Eye className="w-3.5 h-3.5 mr-2" /> Make visible</>
                              ) : (
                                <><EyeOff className="w-3.5 h-3.5 mr-2" /> Hide from friends</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => remove(e)} className="text-rose-600" data-testid={`feed-delete-${e.id}`}>
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  {e.is_self && e.visibility === "hidden" && (
                    <Badge variant="outline" className="mt-1 text-[10px]">Hidden — only you see this</Badge>
                  )}

                  {/* Reactions row + emoji picker */}
                  <div className="mt-3 flex items-center gap-1 flex-wrap">
                    {e.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        type="button"
                        onClick={() => react(e, r.emoji)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs transition-colors tabular-nums ${
                          r.me
                            ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                            : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                        }`}
                        data-testid={`feed-rx-${e.id}-${r.emoji}`}
                      >
                        <span className="text-base leading-none">{r.emoji}</span> {r.count}
                      </button>
                    ))}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:text-slate-800 hover:border-slate-400 text-xs"
                          data-testid={`feed-add-rx-${e.id}`}
                        >
                          <Plus className="w-3.5 h-3.5" /> React
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-2" align="start" data-testid={`feed-rx-picker-${e.id}`}>
                        <div className="grid grid-cols-6 gap-1">
                          {REACTION_EMOJIS.map((em) => (
                            <button
                              key={em}
                              type="button"
                              title={REACTION_LABEL[em] || em}
                              onClick={() => react(e, em)}
                              className="text-lg p-1.5 rounded hover:bg-slate-100 transition-colors"
                              data-testid={`feed-pick-${em}`}
                            >{em}</button>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2 px-1">
                          Only kind, celebratory reactions available. No sarcasm allowed.
                        </p>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="text-center text-[11px] text-slate-500">
        Want fewer notifications about your own achievements? Adjust in{" "}
        <Link to="/student/account" className="text-indigo-600 hover:underline">Account settings</Link>
        .
      </p>
    </div>
  );
}
